import React from 'react';
import styled from '@emotion/styled';
import dayjs from 'dayjs';
import EditIcon from '@mui/icons-material/Edit';
import MessageIcon from '@mui/icons-material/Message';
import { mqUser } from 'utils/responsive';
import { ColorLabel } from 'ui/components/elements/ColorLabel/ColorLabel';

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    border-radius: 10px;
    overflow: hidden;
    background-color: var(--white);
    margin-top: 20px;
`;

const TableHeader = styled.th`
    padding: 12px;
    background-color: var(--main-font-color);
    border: 1px solid var(--gray);
    text-align: left;
    color: var(--white);
    font-size: var(--font-medium);
    font-weight: var(--font-bold);

    ${mqUser('SP')} {
        padding: 8px;
        font-size: var(--font-small);
    }
`;

const TableRow = styled.tr`
    &:nth-of-type(even) {
        background-color: var(--light-gray);
    }

    &:hover {
        background-color: #f0f0f0;
    }
`;

const TableCell = styled.td`
    padding: 12px;
    border: 1px solid #e0e0e0;
    font-size: var(--font-medium);

    ${mqUser('SP')} {
        padding: 8px;
        font-size: var(--font-small);
    }
`;

const EmptyMessage = styled.div`
    text-align: center;
    padding: 40px;
    color: #666;
    font-size: var(--font-medium);
`;

const ProgressBarContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
`;

const ProgressBarWrapper = styled.div`
    flex: 1;
    height: 20px;
    background-color: #e0e0e0;
    border-radius: 10px;
    overflow: hidden;
    position: relative;
`;

const ProgressBar = styled.div<{ percentage: number }>`
    height: 100%;
    width: ${(props) => props.percentage}%;
    background: linear-gradient(90deg, #4caf50 0%, #8bc34a 100%);
    border-radius: 10px;
    transition: width 0.3s ease;
    position: relative;
    overflow: hidden;

    &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.3) 50%, transparent 100%);
        animation: shimmer 2s infinite;
    }

    @keyframes shimmer {
        0% {
            transform: translateX(-100%);
        }
        100% {
            transform: translateX(100%);
        }
    }
`;

const ProgressText = styled.span`
    font-size: var(--font-small);
    font-weight: var(--font-bold);
    color: var(--main-font-color);
    min-width: 27px;
    text-align: right;
`;

const IconButtonContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
`;

const IconButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: none;
    background-color: transparent;
    color: var(--main-color-blue);
    cursor: pointer;
    border-radius: 4px;
    transition: all 0.2s ease;

    &:hover {
        background-color: #f0f0f0;
        color: #0056b3;
    }

    &:active {
        transform: scale(0.95);
    }

    svg {
        width: 20px;
        height: 20px;
    }

    ${mqUser('SP')} {
        width: 28px;
        height: 28px;

        svg {
            width: 18px;
            height: 18px;
        }
    }
`;

const CompanyNameContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const CompanyName = styled.div`
    font-weight: var(--font-bold);
`;

const CountContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const CountItem = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    white-space: nowrap;
`;

const CountLabel = styled.span`
    font-size: var(--font-medium);
    color: #666;
`;

const CountValue = styled.span`
    font-size: var(--font-medium);
    font-weight: var(--font-bold);
    color: var(--main-font-color);
`;

interface RegisteredCompany {
    id: number;
    name: string;
    type: 'seller' | 'buyer';
    registeredAt: string;
    email: string;
    profile_rate: number;
    offer_count: number;
    match_count: number;
}

interface RegisteredCompanyTableProps {
    registeredCompanies: RegisteredCompany[];
    onEdit?: (companyId: number, type: 'seller' | 'buyer') => void;
    onMessage?: (companyId: number, type: 'seller' | 'buyer') => void;
}

export const RegisteredCompanyTable: React.FC<RegisteredCompanyTableProps> = ({
    registeredCompanies,
    onEdit,
    onMessage,
}) => {
    return (
        <Table>
            <thead>
                <tr>
                    <TableHeader>企業名</TableHeader>
                    <TableHeader>登録日</TableHeader>
                    <TableHeader>メールアドレス</TableHeader>
                    <TableHeader>プロフィール登録率</TableHeader>
                    <TableHeader style={{ width: '120px' }}>成績</TableHeader>
                    <TableHeader style={{ textAlign: 'center', width: '100px' }}>操作</TableHeader>
                </tr>
            </thead>
            <tbody>
                {registeredCompanies.length === 0 ? (
                    <tr>
                        <TableCell colSpan={6}>
                            <EmptyMessage>登録済みの企業はありません</EmptyMessage>
                        </TableCell>
                    </tr>
                ) : (
                    registeredCompanies.map((item) => (
                        <TableRow key={item.id}>
                            <TableCell>
                                <CompanyNameContainer>
                                    {item.type === 'seller' ? (
                                        <ColorLabel
                                            color="#ee7800"
                                            backgroundColor="#fafad2"
                                            borderColor="#ee7800"
                                            fontSize="var(--font-small)"
                                            padding="0.2rem 0.6rem"
                                        >
                                            売り手
                                        </ColorLabel>
                                    ) : (
                                        <ColorLabel
                                            color="#006eb0"
                                            backgroundColor="#e5ffff"
                                            borderColor="#006eb0"
                                            fontSize="var(--font-small)"
                                            padding="0.2rem 0.6rem"
                                        >
                                            買い手
                                        </ColorLabel>
                                    )}
                                    <CompanyName>{item.name}</CompanyName>
                                </CompanyNameContainer>
                            </TableCell>
                            <TableCell>
                                {item.registeredAt ? dayjs(item.registeredAt).format('YYYY/MM/DD') : '-'}
                            </TableCell>
                            <TableCell>{item.email}</TableCell>
                            <TableCell>
                                <ProgressBarContainer>
                                    <ProgressBarWrapper>
                                        <ProgressBar percentage={item.profile_rate} />
                                    </ProgressBarWrapper>
                                    <ProgressText>{item.profile_rate}%</ProgressText>
                                </ProgressBarContainer>
                            </TableCell>
                            <TableCell>
                                <CountContainer>
                                    <CountItem>
                                        <CountLabel>オファー数:</CountLabel>
                                        <CountValue>{item.offer_count}</CountValue>
                                    </CountItem>
                                    <CountItem>
                                        <CountLabel>マッチング数:</CountLabel>
                                        <CountValue>{item.match_count}</CountValue>
                                    </CountItem>
                                </CountContainer>
                            </TableCell>
                            <TableCell>
                                <IconButtonContainer>
                                    {onEdit && (
                                        <IconButton
                                            onClick={() => onEdit(item.id, item.type)}
                                            aria-label="編集"
                                            title="編集"
                                        >
                                            <EditIcon />
                                        </IconButton>
                                    )}
                                    {onMessage && (
                                        <IconButton
                                            onClick={() => onMessage(item.id, item.type)}
                                            aria-label="メッセージ"
                                            title="メッセージ"
                                        >
                                            <MessageIcon />
                                        </IconButton>
                                    )}
                                </IconButtonContainer>
                            </TableCell>
                        </TableRow>
                    ))
                )}
            </tbody>
        </Table>
    );
};
